import React from 'react';
import { awayReasonLabel, dayLabel, fieldLabel, formatFieldValue } from '../../utils/timesheetDisplay';
import { moneyOrAbsent } from '../../utils/moneyOrAbsent';
import { formatDateDayMonth } from '../../utils/dateFormat';

/**
 * TIMESHEET ACTIVITY HISTORY — the WORDS (owner 2026-10-29: *"an activity history for each users
 * timesheet and for sub department mass edit time sheets so that its easy to see what was changed,
 * when and by who"*). Pure functions: the server's history rows
 * (`GET /api/timesheets/people/:lineageId/history`, `…/units/:unitId/history`,
 * `server/helpers/timesheetHistory.js`) → the entries `components/ActivityHistoryList.js` renders,
 * and the change lines the reason dialog (`useEditReason.js`) shows. Nothing here fetches, sorts
 * (the server answers newest first) or decides who may see what — `impersonated_by` arrives only
 * for a site admin, and is passed on as `onBehalfOf` only when it did.
 *
 * 💷 Money is printed as the server sends it (`pay.before` / `pay.after`), never computed here.
 */

/** The pseudo-fields the server's diff adds beside the timesheet fields. */
const PSEUDO_LABELS = Object.freeze({ away: 'Away', gag: 'Main job', job_text: 'Main job', notes: 'Notes', run_premium: '6th/7th day' });

export function changeFieldLabel(field) {
  return PSEUDO_LABELS[field] || fieldLabel(field);
}

/** One value as words; a blank is the em dash (the mock's "— → 06:30"). */
export function changeValueText(field, value, unitName) {
  if (value === null || value === undefined || value === '') return '—';
  if (field === 'away') return awayReasonLabel(value);
  // The server's run-reset diff (`run_premium`: 6 or 7) — the premium a day is paid at.
  if (field === 'run_premium') return `${value}th day`;
  if (field === 'gag' || field === 'job_text' || field === 'notes') return String(value);
  return formatFieldValue(field, value, unitName) ?? '—';
}

/** "Mon 2 Nov · Unit wrap 18:00 → 20:00" */
export function changeLine(change, unitName, { withDate = true } = {}) {
  const text = `${changeFieldLabel(change.field)} ${changeValueText(change.field, change.old, unitName)} → ${changeValueText(change.field, change.new, unitName)}`;
  return withDate && change.date ? `${dayLabel(change.date)} · ${text}` : text;
}

/** Every change of one save as one line per DAY ("Mon 2 Nov · Unit wrap 18:00 → 20:00 · Camera O/T 0 h → 1 h"). */
export function changesSummary(changes, unitName) {
  const byDate = new Map();
  for (const c of changes || []) {
    if (!byDate.has(c.date)) byDate.set(c.date, []);
    byDate.get(c.date).push(changeLine(c, unitName, { withDate: false }));
  }
  return [...byDate.entries()].map(([date, parts]) => `${date ? `${dayLabel(date)} · ` : ''}${parts.join(' · ')}`);
}

/** Several lines as ONE node (the shared list renders a node as a block, a string as one `<p>`). */
function detailNode(lines) {
  if (!lines || lines.length === 0) return undefined;
  return <>{lines.map((l, i) => <span key={i} className="block">{l}</span>)}</>;
}

/** The pay change a row carries, in the shared list's `money` shape — or null. */
function moneyOf(pay) {
  if (!pay || typeof pay !== 'object') return null;
  if (pay.before == null && pay.after == null) return null;
  return { before: pay.before, after: pay.after };
}

const unitNamesOf = (ids, unitName) => (ids || []).map((id) => unitName(Number(id)) || `Unit ${id}`).join(', ');
const dateRange = (dates) => {
  const ds = [...(dates || [])].sort();
  if (ds.length === 0) return '';
  if (ds.length === 1) return dayLabel(ds[0]);
  return `${dayLabel(ds[0])}–${dayLabel(ds[ds.length - 1])}`;
};

/** The headline, the rail dot and the filter group each person-week action belongs to. */
const ACTIONS = Object.freeze({
  person_day_manager: { tag: 'Manager edit', dot: 'bg-yellow-500', group: 'times' },
  person_day_self: { tag: 'Own entry', dot: 'bg-gray-400', group: 'times' },
  unit_edit_applied: { tag: 'Unit edit', dot: 'bg-blue-500', group: 'unit' },
  conflict_resolved: { tag: 'Conflict settled', dot: 'bg-amber-500', group: 'times' },
  // RC-2: a crew member's own entry moved this week's 6th/7th-day pay (system reason names it).
  crew_entry_knock_on: { tag: 'Moved by an own entry', dot: 'bg-amber-500', group: 'times' },
  away_created: { tag: 'Away set', dot: 'bg-slate-500', group: 'times' },
  away_ended: { tag: 'Away ended', dot: 'bg-slate-500', group: 'times' },
  week_submitted: { tag: 'Submitted', dot: 'bg-green-500', group: 'status' },
  week_reopened: { tag: 'Reopened', dot: 'bg-orange-500', group: 'status' },
  week_sent: { tag: 'Sent in by the crew member', dot: 'bg-indigo-500', group: 'status' },
  week_taken: { tag: 'Taken over', dot: 'bg-indigo-500', group: 'status' },
  week_exported: { tag: 'Exported to payroll', dot: 'bg-purple-500', group: 'status' },
});

/** The mock's filter chips: All · Times · Status · Unit edits. */
export const HISTORY_FILTERS = Object.freeze([
  { id: 'all', label: 'All' },
  { id: 'times', label: 'Times' },
  { id: 'status', label: 'Status' },
  { id: 'unit', label: 'Unit edits' },
]);

const EXPORT_FORMAT_LABELS = Object.freeze({ csv: 'CSV', pdf: 'PDF', print: 'Print' });

/**
 * One person-week's history rows → `ActivityHistoryList` entries (newest first, as given), each
 * carrying its filter `group`. `unitName(id)` resolves a unit id.
 */
export function personWeekHistoryEntries(rows, unitName = () => null) {
  return (rows || []).map((r) => {
    const d = r.details || {};
    const meta = ACTIONS[r.action] || { tag: r.action, dot: 'bg-gray-400', group: 'times' };
    let tag = meta.tag;
    let lines = [];
    let money = null;
    if (r.action === 'unit_edit_applied') {
      tag = `Unit edit · ${unitNamesOf(d.sub_department_ids, unitName)}`;
      lines = changesSummary(d.changes, unitName);
      money = moneyOf(d.pay);
    } else if (r.action === 'crew_entry_knock_on') {
      lines = changesSummary(d.changes, unitName);
      money = moneyOf(d.pay);
    } else if (r.action === 'person_day_manager' || r.action === 'person_day_self') {
      lines = Array.isArray(d.changes) && d.changes.length ? changesSummary(d.changes, unitName)
        : [r.work_date ? `${dayLabel(r.work_date)} · no change to the week` : 'No change to the week'];
      money = r.action === 'person_day_manager' ? moneyOf(d.pay) : null;
    } else if (r.action === 'away_created' || r.action === 'away_ended') {
      const range = `${d.from_date ? formatDateDayMonth(d.from_date) : '?'} → ${d.to_date ? formatDateDayMonth(d.to_date) : 'until further notice'}`;
      lines = [`${awayReasonLabel(d.reason)} · ${range}`];
      money = moneyOf(d.pay);
    } else if (r.action === 'week_submitted') {
      if (d.resubmit) tag = 'Resubmitted';
      lines = d.total != null ? [`Week pay ${moneyOrAbsent(d.total)}`] : [];
    } else if (r.action === 'week_exported') {
      lines = [`Payroll run (${EXPORT_FORMAT_LABELS[d.format] || d.format})${d.total != null ? ` · ${moneyOrAbsent(d.total)}` : ''}${d.basis === 'projected' ? ' · projected' : ''}`];
    } else if (r.action === 'conflict_resolved') {
      const settledLine = (date, field, choice) => `${dayLabel(date)} · ${changeFieldLabel(field)}: ${choice === 'unit' ? 'follows the unit' : 'keeps their own value'}`;
      // Since 2026-10-30 (review m-2) one row per person-week, written by the edit tracker: what was
      // settled (`resolved`) and the pay it moved. Older rows are one per item (`field`/`choice`).
      if (Array.isArray(d.resolved)) {
        lines = d.resolved.map((x) => settledLine(x.date, x.field, x.choice));
        money = moneyOf(d.pay);
      } else {
        lines = [settledLine(r.work_date, d.field, d.choice)];
      }
    }
    if (d.submitted === true && r.action !== 'week_submitted') tag = `${tag} · submitted week`;
    return {
      id: r.id,
      at: r.at,
      actor: r.actor || 'Someone',
      onBehalfOf: r.impersonated_by || undefined,
      tag,
      details: detailNode(lines),
      reason: r.reason || (r.action === 'week_reopened' ? d.reason : undefined) || undefined,
      money,
      dotClass: meta.dot,
      group: meta.group,
    };
  });
}

/** Keep the entries of one filter (`all` keeps everything). */
export function filterHistory(entries, filter) {
  if (!filter || filter === 'all') return entries;
  return entries.filter((e) => e.group === filter);
}

/** One unit's history (the server's grouped saves) → entries: who, the values, who it reached. */
export function unitHistoryEntries(rows, unitName = () => null) {
  return (rows || []).map((g) => {
    const valueLines = Object.entries(g.values || {}).map(([field, list]) => {
      const distinct = new Map();
      for (const v of list) distinct.set(`${v.old}|${v.new}`, v);
      const parts = [...distinct.values()].map((v) => `${v.old === undefined ? '' : `${changeValueText(field, v.old, unitName)} → `}${changeValueText(field, v.new, unitName)}`);
      return `${changeFieldLabel(field)} ${parts.join(', ')}`;
    });
    const affected = g.affected || [];
    const submitted = affected.filter((a) => a.submitted);
    const reach = affected.length === 0 ? 'Affected nobody’s week'
      : `Affected ${affected.length} ${affected.length === 1 ? 'person-week' : 'person-weeks'} · ${submitted.length === 0 ? 'no submitted weeks'
        : `${submitted.length} submitted (${submitted.map((a) => a.name || 'someone').join(', ')})`}`;
    const priced = affected.filter((a) => a.pay && Number.isFinite(Number(a.pay.before)) && Number.isFinite(Number(a.pay.after)));
    const money = priced.length
      ? { before: priced.reduce((s, a) => s + Number(a.pay.before), 0), after: priced.reduce((s, a) => s + Number(a.pay.after), 0) }
      : null;
    return {
      id: g.id,
      at: g.at,
      actor: g.actor || 'Someone',
      onBehalfOf: g.impersonated_by || undefined,
      tag: `${unitName(Number(g.sub_department_id)) || 'Unit'} · ${dateRange(g.dates)}`,
      details: detailNode([...valueLines, ...(g.notes ? [`Notes: ${g.notes}`] : []), reach]),
      reason: g.reason || undefined,
      money,
      dotClass: 'bg-blue-500',
    };
  });
}
